import React from "react";
import { View, Text, TouchableOpacity, StyleSheet, Alert } from "react-native";
import { useUpdates } from "../hooks/useUpdates";
import { MaterialIcons } from "@expo/vector-icons";
import { useTranslation } from "react-i18next";

interface UpdateNotificationProps {
  showAlways?: boolean;
  onUpdateApplied?: () => void;
}

export const UpdateNotification: React.FC<UpdateNotificationProps> = ({
  showAlways = false,
  onUpdateApplied,
}) => {
  const { t } = useTranslation();
  const {
    updateInfo,
    isChecking,
    checkForUpdates,
    applyUpdate,
    channel,
    runtimeVersion,
  } = useUpdates();

  const handleApplyUpdate = async () => {
    Alert.alert(
      t("Apply Update"),
      t("This will restart the app to apply the update. Continue?"),
      [
        { text: t("Cancel"), style: "cancel" },
        {
          text: t("Apply"),
          style: "destructive",
          onPress: async () => {
            const success = await applyUpdate();
            if (success && onUpdateApplied) {
              onUpdateApplied();
            }
          },
        },
      ],
    );
  };

  const handleCheckForUpdates = () => {
    checkForUpdates();
  };

  // Don't show anything if no updates and not showing always
  if (!showAlways && !updateInfo.isAvailable && !updateInfo.error) {
    return null;
  }

  return (
    <View style={styles.container}>
      {updateInfo.isAvailable && (
        <View style={styles.updateAvailable}>
          <MaterialIcons name="system-update" size={24} color="#4CAF50" />
          <View style={styles.updateInfo}>
            <Text style={styles.updateTitle}>{t("Update Available")}</Text>
            <Text style={styles.updateVersion}>
              {t("Version")}:{" "}
              {updateInfo.manifest?.metadata?.version || t("New version")}
            </Text>
            <Text style={styles.updateChannel}>
              {t("Channel")}: {channel}
            </Text>
          </View>
          <TouchableOpacity
            style={styles.applyButton}
            onPress={handleApplyUpdate}
            disabled={updateInfo.isUpdateLoading}
          >
            <Text style={styles.applyButtonText}>
              {updateInfo.isUpdateLoading ? t("Applying...") : t("Apply")}
            </Text>
          </TouchableOpacity>
        </View>
      )}

      {updateInfo.error && (
        <View style={styles.errorContainer}>
          <MaterialIcons name="error" size={24} color="#F44336" />
          <Text style={styles.errorText}>{updateInfo.error}</Text>
        </View>
      )}

      {updateInfo.isDevelopment && (
        <View style={styles.developmentContainer}>
          <MaterialIcons name="developer-mode" size={20} color="#FF9800" />
          <View style={styles.developmentText}>
            <Text style={styles.developmentTitle}>{t("Development Mode")}</Text>
            <Text style={styles.developmentDetail}>
              {t(
                "Updates are simulated in development builds. Build a preview/production app to test real updates.",
              )}
            </Text>
          </View>
        </View>
      )}

      {showAlways && (
        <View style={styles.infoContainer}>
          <MaterialIcons name="info" size={20} color="#2196F3" />
          <View style={styles.infoText}>
            <Text style={styles.infoTitle}>{t("App Updates")}</Text>
            <Text style={styles.infoDetail}>
              {t("Channel")}: {channel}
            </Text>
            <Text style={styles.infoDetail}>
              {t("Runtime")}: {runtimeVersion}
            </Text>
          </View>
          <TouchableOpacity
            style={styles.checkButton}
            onPress={handleCheckForUpdates}
            disabled={isChecking}
          >
            <Text style={styles.checkButtonText}>
              {isChecking ? t("Checking...") : t("Check")}
            </Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 16,
  },
  updateAvailable: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#E8F5E8",
    padding: 16,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: "#4CAF50",
  },
  updateInfo: {
    flex: 1,
    marginLeft: 12,
  },
  updateTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#2E7D32",
  },
  updateVersion: {
    fontSize: 14,
    color: "#388E3C",
    marginTop: 2,
  },
  updateChannel: {
    fontSize: 12,
    color: "#689F38",
    marginTop: 2,
  },
  applyButton: {
    backgroundColor: "#4CAF50",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
  },
  applyButtonText: {
    color: "white",
    fontWeight: "bold",
    fontSize: 14,
  },
  errorContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFEBEE",
    padding: 16,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: "#F44336",
  },
  errorText: {
    flex: 1,
    marginLeft: 12,
    color: "#C62828",
    fontSize: 14,
  },
  infoContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#E3F2FD",
    padding: 16,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: "#2196F3",
  },
  infoText: {
    flex: 1,
    marginLeft: 12,
  },
  infoTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#1565C0",
  },
  infoDetail: {
    fontSize: 12,
    color: "#1976D2",
    marginTop: 2,
  },
  checkButton: {
    backgroundColor: "#2196F3",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
  },
  checkButtonText: {
    color: "white",
    fontWeight: "bold",
    fontSize: 14,
  },
  developmentContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFF3E0",
    padding: 16,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: "#FF9800",
    marginTop: 8,
  },
  developmentText: {
    flex: 1,
    marginLeft: 12,
  },
  developmentTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#E65100",
  },
  developmentDetail: {
    fontSize: 12,
    color: "#F57C00",
    marginTop: 2,
  },
});

export default UpdateNotification;
